import { createContext, useContext } from "react";
import type {
	setUpTOTP,
	verifyTOTPSetup,
	updateMFAPreference,
	fetchMFAPreference,
	associateWebAuthnCredential,
	listWebAuthnCredentials,
	deleteWebAuthnCredential,
	signOut,
	AuthSession,
	FetchAuthSessionOptions,
} from "aws-amplify/auth";

import type { PasskeyMFAPreference } from "../utils/updatePasskeyMFAPreference";

export interface AmplifyAuthFunctions {
	// Narrowed from Amplify's overloads to the client-side signature.
	fetchAuthSession: (options?: FetchAuthSessionOptions) => Promise<AuthSession>;
	signOut: typeof signOut;
	setUpTOTP: typeof setUpTOTP;
	verifyTOTPSetup: typeof verifyTOTPSetup;
	updateMFAPreference: typeof updateMFAPreference;
	fetchMFAPreference: typeof fetchMFAPreference;
	associateWebAuthnCredential: typeof associateWebAuthnCredential;
	listWebAuthnCredentials: typeof listWebAuthnCredentials;
	deleteWebAuthnCredential: typeof deleteWebAuthnCredential;
	updatePasskeyMFAPreference: (
		preference: PasskeyMFAPreference,
	) => Promise<void>;
}

const AmplifyAuthContext = createContext<AmplifyAuthFunctions | null>(null);

interface AmplifyAuthProviderProps {
	children: React.ReactNode;
	value: AmplifyAuthFunctions;
}

export function AmplifyAuthProvider({
	children,
	value,
}: AmplifyAuthProviderProps) {
	return (
		<AmplifyAuthContext.Provider value={value}>
			{children}
		</AmplifyAuthContext.Provider>
	);
}

export function useAmplifyAuth(): AmplifyAuthFunctions {
	const context = useContext(AmplifyAuthContext);
	if (!context) {
		throw new Error(
			"useAmplifyAuth must be used within an AmplifyAuthProvider",
		);
	}
	return context;
}
